import React, { useState, useMemo } from 'react';
import { FaEdit, FaTrash, FaPlus, FaCheck, FaTimes, FaArrowLeft, FaStar, FaRegStar, FaSearch, FaFilter, FaEllipsisH, FaSortUp, FaSortDown, FaSort, FaArrowRight } from 'react-icons/fa';
import { useWatchlist } from '../context/WatchlistContext';
import './WatchlistPage.css';
import StockLogo from './StockLogo';

const WatchlistPage = ({ onBack }) => {
  const { 
    watchlists, 
    activeWatchlistId, 
    setActiveWatchlistId,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    removeFromWatchlist
  } = useWatchlist();

  const [editingWatchlistId, setEditingWatchlistId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const activeWatchlist = watchlists.find(wl => wl.id === activeWatchlistId) || watchlists[0];
  const watchlistData = activeWatchlist?.stocks || [];

  // Filtered and sorted data
  const processedData = useMemo(() => {
    let data = [...watchlistData];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter(s => 
        (s.symbol || '').toLowerCase().includes(query) ||
        (s.name || '').toLowerCase().includes(query)
      );
    }

    // Sort
    if (sortConfig.key) {
      data.sort((a, b) => {
        let aVal, bVal;
        switch (sortConfig.key) {
          case 'symbol':
            aVal = (a.symbol || '').toLowerCase();
            bVal = (b.symbol || '').toLowerCase();
            return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          case 'price':
            aVal = a.ltp || 0;
            bVal = b.ltp || 0;
            break;
          case 'change':
            aVal = a.change || a.percentage_change || 0;
            bVal = b.change || b.percentage_change || 0;
            break;
          case 'changePercent':
            aVal = a.changePercent || a.percentage_change || 0;
            bVal = b.changePercent || b.percentage_change || 0;
            break;
          default:
            return 0;
        }
        if (typeof aVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return 0;
      });
    }

    return data;
  }, [watchlistData, searchQuery, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="sort-icon active" /> 
      : <FaSortDown className="sort-icon active" />;
  };

  const handleStartEdit = (watchlist) => {
    setEditingWatchlistId(watchlist.id);
    setEditingName(watchlist.name);
  };

  const handleSaveEdit = () => {
    if (editingName.trim()) {
      renameWatchlist(editingWatchlistId, editingName.trim());
    }
    setEditingWatchlistId(null);
    setEditingName('');
  };

  const handleCancelEdit = () => {
    setEditingWatchlistId(null);
    setEditingName('');
  };

  const handleCreateNew = () => {
    if (newWatchlistName.trim()) {
      const newId = createWatchlist(newWatchlistName.trim());
      setActiveWatchlistId(newId);
      setNewWatchlistName('');
      setShowCreateNew(false);
    }
  };

  const handleDeleteWatchlist = (id) => {
    if (window.confirm('Are you sure you want to delete this watchlist?')) {
      deleteWatchlist(id);
    }
  };

  const handleRemoveStock = (symbol) => {
    if (window.confirm('Remove this stock from watchlist?')) {
      removeFromWatchlist(symbol, activeWatchlistId);
    }
  };

  const calculateStats = () => {
    const gainers = watchlistData.filter(s => (s.change || s.percentage_change || 0) > 0).length;
    const losers = watchlistData.filter(s => (s.change || s.percentage_change || 0) < 0).length;
    const total = watchlistData.length;
    const avgChange = total > 0 
      ? watchlistData.reduce((sum, s) => sum + (s.changePercent || s.percentage_change || 0), 0) / total 
      : 0;
    
    return { gainers, losers, total, avgChange };
  };

  const stats = calculateStats();

  // Check if market is open (9:15 AM - 3:30 PM IST, Mon-Fri)
  const isMarketOpen = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const day = now.getDay();
    const time = hours * 60 + minutes;
    return day >= 1 && day <= 5 && time >= 555 && time <= 930;
  };

  return (
    <div className="wl-page">
      {/* Top Bar */}
      <div className="wl-topbar">
        <div className="wl-topbar-left">
          <button className="wl-back-btn" onClick={onBack}>
            <FaArrowLeft /> <span>Back to Dashboard</span>
          </button>
          <div className="wl-title-block">
            <h1 className="wl-main-title">My Watchlists</h1>
            <p className="wl-subtitle">
              Track. Analyze. Stay Ahead.
              <span className={`wl-market-status ${isMarketOpen() ? 'open' : 'closed'}`}>
                <span className="wl-market-dot"></span>
                {isMarketOpen() ? 'Market is Open' : 'Market is Closed'}
              </span>
            </p>
          </div>
        </div>
        <div className="wl-topbar-right">
          <button className="wl-track-btn">
            <FaStar /> <span>Track your favorite stocks</span>
          </button>
        </div>
      </div>

      {/* Watchlist Tabs */}
      <div className="wl-tabs-row">
        {watchlists.map(wl => (
          <div
            key={wl.id}
            className={`wl-tab ${wl.id === activeWatchlistId ? 'active' : ''}`}
            onClick={() => setActiveWatchlistId(wl.id)}
          >
            {editingWatchlistId === wl.id ? (
              <div className="wl-tab-edit" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  autoFocus
                  className="wl-tab-edit-input"
                />
                <button onClick={handleSaveEdit} className="wl-tab-action save"><FaCheck /></button>
                <button onClick={handleCancelEdit} className="wl-tab-action cancel"><FaTimes /></button>
              </div>
            ) : (
              <>
                <span className="wl-tab-icon">📋</span>
                <span className="wl-tab-name">{wl.name}</span>
                <span className="wl-tab-count">{wl.stocks.length}</span>
                <div className="wl-tab-hover-actions">
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleStartEdit(wl); }} 
                    className="wl-tab-hover-btn"
                    title="Rename"
                  >
                    <FaEdit />
                  </button>
                  {watchlists.length > 1 && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleDeleteWatchlist(wl.id); }} 
                      className="wl-tab-hover-btn delete"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        
        {showCreateNew ? (
          <div className="wl-tab create-mode">
            <input
              type="text"
              placeholder="Watchlist name..."
              value={newWatchlistName}
              onChange={(e) => setNewWatchlistName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateNew();
                if (e.key === 'Escape') { setShowCreateNew(false); setNewWatchlistName(''); }
              }}
              autoFocus
              className="wl-tab-create-input"
            />
            <button onClick={handleCreateNew} className="wl-tab-action save"><FaCheck /></button>
            <button onClick={() => { setShowCreateNew(false); setNewWatchlistName(''); }} className="wl-tab-action cancel"><FaTimes /></button>
          </div>
        ) : (
          <button className="wl-tab add-new" onClick={() => setShowCreateNew(true)}>
            <FaPlus /> <span>New Watchlist</span>
          </button>
        )}
      </div>

      {/* Summary Card */}
      <div className="wl-summary-card">
        <div className="wl-summary-header">
          <div className="wl-summary-title-row">
            <span className="wl-summary-icon">📊</span>
            <div>
              <h2 className="wl-summary-title">Watchlist Summary</h2>
              <p className="wl-summary-desc">Real-time overview of your watchlist performance</p>
            </div>
          </div>
          <div className="wl-summary-updated">
            <span>Last updated: Real-time</span>
            <span className="wl-live-dot"></span>
          </div>
        </div>

        <div className="wl-stats-grid">
          <div className="wl-stat-card gainers">
            <div className="wl-stat-icon-wrap gainers-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 17L9 11L13 15L21 7" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M15 7H21V13" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="wl-stat-info">
              <span className="wl-stat-label">GAINERS</span>
              <span className="wl-stat-value positive">{stats.gainers}</span>
              <span className="wl-stat-sub positive">+{stats.gainers > 0 ? ((stats.gainers / stats.total) * 100).toFixed(2) : '0.00'}% vs yesterday</span>
            </div>
          </div>

          <div className="wl-stat-card losers">
            <div className="wl-stat-icon-wrap losers-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 7L9 13L13 9L21 17" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M15 17H21V11" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="wl-stat-info">
              <span className="wl-stat-label">LOSERS</span>
              <span className="wl-stat-value negative">{stats.losers}</span>
              <span className="wl-stat-sub positive">+{stats.losers > 0 ? ((stats.losers / stats.total) * 100).toFixed(2) : '0.00'}% vs yesterday</span>
            </div>
          </div>

          <div className="wl-stat-card total">
            <div className="wl-stat-icon-wrap total-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#6e2cff" strokeWidth="2"/><circle cx="12" cy="12" r="5" stroke="#a78bfa" strokeWidth="2"/><circle cx="12" cy="12" r="1.5" fill="#c4b5fd"/></svg>
            </div>
            <div className="wl-stat-info">
              <span className="wl-stat-label">TOTAL STOCKS</span>
              <span className="wl-stat-value">{stats.total}</span>
              <span className="wl-stat-sub">Across all watchlists</span>
            </div>
          </div>

          <div className="wl-stat-card avg">
            <div className="wl-stat-icon-wrap avg-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="12" width="4" height="8" rx="1" fill="#6e2cff"/><rect x="10" y="8" width="4" height="12" rx="1" fill="#8b5cf6"/><rect x="17" y="4" width="4" height="16" rx="1" fill="#a78bfa"/></svg>
            </div>
            <div className="wl-stat-info">
              <span className="wl-stat-label">AVG CHANGE</span>
              <span className={`wl-stat-value ${stats.avgChange >= 0 ? 'positive' : 'negative'}`}>
                {stats.avgChange >= 0 ? '+' : ''}{stats.avgChange.toFixed(2)}%
              </span>
              <span className="wl-stat-sub">Market average</span>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="wl-table-card">
        <div className="wl-table-header">
          <div className="wl-table-header-left">
            <span className="wl-table-icon">📋</span>
            <h2 className="wl-table-title">{activeWatchlist.name}</h2>
          </div>
          <div className="wl-table-header-right">
            <div className="wl-search-box">
              <FaSearch className="wl-search-icon" />
              <input 
                type="text" 
                placeholder="Search stocks..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="wl-search-input"
              />
            </div>
            <button 
              className={`wl-icon-btn ${showFilterMenu ? 'active' : ''}`}
              onClick={() => { setShowFilterMenu(!showFilterMenu); setShowMoreMenu(false); }}
              title="Filter"
            >
              <FaFilter />
            </button>
            <button 
              className={`wl-icon-btn ${showMoreMenu ? 'active' : ''}`}
              onClick={() => { setShowMoreMenu(!showMoreMenu); setShowFilterMenu(false); }}
              title="More options"
            >
              <FaEllipsisH />
            </button>
          </div>
        </div>

        {/* Filter dropdown */}
        {showFilterMenu && (
          <div className="wl-dropdown-menu">
            <button onClick={() => { handleSort('symbol'); setShowFilterMenu(false); }}>Sort by Name</button>
            <button onClick={() => { handleSort('price'); setShowFilterMenu(false); }}>Sort by Price</button>
            <button onClick={() => { handleSort('changePercent'); setShowFilterMenu(false); }}>Sort by Change %</button>
          </div>
        )}

        {/* More dropdown */}
        {showMoreMenu && (
          <div className="wl-dropdown-menu more">
            <button onClick={() => { setSortConfig({ key: null, direction: 'asc' }); setSearchQuery(''); setShowMoreMenu(false); }}>Reset Filters</button>
          </div>
        )}

        <div className="wl-showing-badge">
          Showing {processedData.length} stocks
        </div>

        {/* Table */}
        <div className="wl-table-wrap">
          <table className="wl-table">
            <thead>
              <tr>
                <th className="wl-th-star">☆</th>
                <th className="wl-th-stock" onClick={() => handleSort('symbol')}>
                  STOCK {renderSortIcon('symbol')}
                </th>
                <th className="wl-th-price" onClick={() => handleSort('price')}>
                  PRICE {renderSortIcon('price')}
                </th>
                <th className="wl-th-change" onClick={() => handleSort('change')}>
                  CHANGE {renderSortIcon('change')}
                </th>
                <th className="wl-th-changepct" onClick={() => handleSort('changePercent')}>
                  CHANGE % {renderSortIcon('changePercent')}
                </th>
                <th className="wl-th-range">DAY RANGE {renderSortIcon('price')}</th>
                <th className="wl-th-actions">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {processedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="wl-empty-td">
                    <div className="wl-empty-state">
                      <div className="wl-empty-glow">
                        <div className="wl-empty-ring ring-1"></div>
                        <div className="wl-empty-ring ring-2"></div>
                        <div className="wl-empty-ring ring-3"></div>
                        <FaRegStar className="wl-empty-star" />
                      </div>
                      <h3>No stocks in this watchlist</h3>
                      <p>Start building your watchlist by adding stocks you want to track</p>
                      <button className="wl-explore-btn" onClick={onBack}>
                        Explore Stocks <FaArrowRight />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                processedData.map((stock, index) => {
                  const change = stock.change || stock.percentage_change || 0;
                  const changePercent = stock.changePercent || stock.percentage_change || 0;
                  const ltp = stock.ltp || 0;
                  const isPositive = change >= 0;
                  const dayHigh = stock.dayHigh || ltp * 1.02;
                  const dayLow = stock.dayLow || ltp * 0.98;
                  const range = dayHigh - dayLow;
                  const progress = range > 0 ? ((ltp - dayLow) / range) * 100 : 50;
                  
                  return (
                    <tr key={stock.symbol || index} className="wl-stock-row">
                      <td className="wl-td-star">
                        <FaStar className="wl-star-filled" />
                      </td>
                      <td className="wl-td-stock">
                        <div className="wl-stock-info-container" style={{ display: 'flex', alignItems: 'center' }}>
                          <StockLogo symbol={stock.symbol} size={28} />
                          <div className="wl-stock-info">
                            <span className="wl-stock-symbol">{stock.symbol}</span>
                            <span className="wl-stock-name">{stock.name || stock.symbol}</span>
                          </div>
                        </div>
                      </td>
                      <td className="wl-td-price">
                        {ltp ? `₹${ltp.toFixed(2)}` : '—'}
                      </td>
                      <td className={`wl-td-change ${isPositive ? 'positive' : 'negative'}`}>
                        {change ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}` : '—'}
                      </td>
                      <td className={`wl-td-changepct ${isPositive ? 'positive' : 'negative'}`}>
                        <span className={`wl-change-badge ${isPositive ? 'positive' : 'negative'}`}>
                          {changePercent ? `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%` : '—'}
                        </span>
                      </td>
                      <td className="wl-td-range">
                        <div className="wl-range-bar">
                          <div className="wl-range-track">
                            <div 
                              className={`wl-range-fill ${isPositive ? 'positive' : 'negative'}`}
                              style={{ width: `${Math.min(Math.max(progress, 5), 95)}%` }}
                            ></div>
                            <div 
                              className="wl-range-thumb"
                              style={{ left: `${Math.min(Math.max(progress, 5), 95)}%` }}
                            ></div>
                          </div>
                          <div className="wl-range-labels">
                            <span>{dayLow ? dayLow.toFixed(0) : '—'}</span>
                            <span>{dayHigh ? dayHigh.toFixed(0) : '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="wl-td-actions">
                        <button 
                          className="wl-remove-btn"
                          onClick={() => handleRemoveStock(stock.symbol)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WatchlistPage;